(() => {
  const VERSION = "0.0.6";
  console.info(`[relief-customizer] script v${VERSION} loaded`);

  let initialized = false;

  const WORKER_BASE_URL = "https://relief-generator.zestyboys.workers.dev";
  const WORKER_UPLOAD_URL = `${WORKER_BASE_URL}/api/upload`;
  const WORKER_GENERATE_URL = `${WORKER_BASE_URL}/api/generate`;

  const WIDTH_MM = 100;
  const DEPTH_MM = 6;
  const BASE_MM = 2;
  const MAX_GRID_RESOLUTION = 144;

  const tryInit = () => {
    if (initialized) return true;

    const form = document.querySelector("#relief-form");
    if (!form) return false;

    const imageInput = form.querySelector("#relief-image-input");
    const frameSelect = document.querySelector("#relief-frame-style");
    const statusEl = document.querySelector("#relief-status");
    const previewImg = document.querySelector("#relief-preview-image");
    const thumbWrap = document.querySelector("#relief-thumb");
    const thumbImg = document.querySelector("#relief-thumb-image");
    const viewer = document.querySelector("#relief-3d-preview");
    const addToCartBtn = document.querySelector("#relief-add-to-cart");
    const cropperWrap = document.querySelector("#relief-cropper");
    const cropImage = document.querySelector("#relief-crop-image");
    const applyCropBtn = document.querySelector("#relief-apply-crop");
    const resetCropBtn = document.querySelector("#relief-reset-crop");

    if (
      !imageInput ||
      !frameSelect ||
      !statusEl ||
      !previewImg ||
      !addToCartBtn ||
      !cropperWrap ||
      !cropImage ||
      !applyCropBtn ||
      !resetCropBtn
    ) {
      console.warn("[relief-customizer] missing DOM nodes, wait for section render");
      return false;
    }

    const variantId = window.reliefProductVariantId;
    if (!variantId) {
      console.warn("[relief-customizer] reliefProductVariantId not set.");
    }

    let originalFile = null;
    let preparedFile = null;
    let cropperInstance = null;
    let cropperLoading = false;
    let latestAnalysis = null;
    let threeRenderer = null;
    let threeScene = null;
    let threeCamera = null;
    let viewerResizeHandler = null;

    const setStatus = (msg) => {
      statusEl.textContent = msg || "";
    };

    const ensureCropper = async () => {
      if (window.Cropper || cropperLoading) {
        while (cropperLoading && !window.Cropper) {
          await new Promise((resolve) => setTimeout(resolve, 60));
        }
        return;
      }
      cropperLoading = true;
      const cssHref = "https://cdn.jsdelivr.net/npm/cropperjs@1.5.13/dist/cropper.min.css";
      const jsSrc = "https://cdn.jsdelivr.net/npm/cropperjs@1.5.13/dist/cropper.min.js";
      if (!document.querySelector(`link[data-relief-cropper]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = cssHref;
        link.dataset.reliefCropper = "true";
        document.head.appendChild(link);
      }
      await new Promise((resolve, reject) => {
        if (window.Cropper) return resolve();
        const script = document.createElement("script");
        script.src = jsSrc;
        script.async = true;
        script.dataset.reliefCropper = "true";
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Cropper.js"));
        document.head.appendChild(script);
      }).catch((err) => {
        console.error(err);
        setStatus("Failed to load cropper. Try reloading the page.");
      });
      cropperLoading = false;
    };

    const destroyCropper = () => {
      if (cropperInstance) {
        cropperInstance.destroy();
        cropperInstance = null;
      }
    };

    const showCropper = async (file) => {
      console.debug("[relief-customizer] initializing cropper");
      cropperWrap.hidden = false;
      setStatus("Loading cropper...");

      const objectUrl = URL.createObjectURL(file);

      const initializeCropper = () => {
        if (!window.Cropper) return;
        destroyCropper();
        cropperInstance = new Cropper(cropImage, {
          viewMode: 1,
          autoCropArea: 0.8,
          responsive: true,
          background: true,
          guides: true,
          highlight: true,
          movable: true,
          zoomable: true,
        });
        setStatus("Adjust the crop area, then apply.");
      };

      cropImage.onload = () => {
        initializeCropper();
        URL.revokeObjectURL(objectUrl);
      };
      cropImage.src = objectUrl;

      await ensureCropper();
      if (!window.Cropper) {
        preparedFile = file;
        setStatus("Cropper unavailable. Proceeding with original image.");
        return;
      }

      if (cropImage.complete) {
        initializeCropper();
      }
    };

    const getCroppedFile = async () => {
      if (!cropperInstance || !window.Cropper) {
        return originalFile;
      }
      const canvas = cropperInstance.getCroppedCanvas({
        maxWidth: 1024,
        maxHeight: 1024,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      if (!canvas) return originalFile;

      const type = originalFile?.type || "image/png";
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Crop failed"));
              return;
            }
            const fileName = originalFile?.name || "relief-upload.png";
            resolve(new File([blob], fileName, { type }));
          },
          type,
          0.92,
        );
      });
    };

    const resetCropState = () => {
      destroyCropper();
      cropperWrap.hidden = true;
      preparedFile = originalFile;
    };

    const loadThree = async () => {
      if (!window.__reliefThreePromise) {
        window.__reliefThreePromise = (async () => {
          const threeModule = await import("https://esm.sh/three@0.157.0?target=es2020");
          const controlsModule = await import(
            "https://esm.sh/three@0.157.0/examples/jsm/controls/OrbitControls?target=es2020"
          );
          const THREE = threeModule?.default ?? threeModule;
          const OrbitControls =
            controlsModule?.OrbitControls ||
            controlsModule?.default?.OrbitControls ||
            controlsModule;
          if (!THREE || !OrbitControls) {
            throw new Error("OrbitControls unavailable");
          }
          return { THREE, OrbitControls };
        })();
      }
      return window.__reliefThreePromise;
    };

    const disposeThree = () => {
      if (viewerResizeHandler) {
        window.removeEventListener("resize", viewerResizeHandler);
        viewerResizeHandler = null;
      }
      if (threeRenderer) {
        threeRenderer.dispose();
        threeRenderer = null;
      }
      threeScene = null;
      threeCamera = null;
    };

    const base64ToUint8 = (value) => {
      const binary = atob(value);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    };

    const uint8ToBase64 = (array) => {
      const chunk = 0x8000;
      let result = "";
      for (let i = 0; i < array.length; i += chunk) {
        const sub = array.subarray(i, Math.min(i + chunk, array.length));
        result += String.fromCharCode.apply(null, sub);
      }
      return btoa(result);
    };

    const smoothHeightMap = (data, width, height, iterations = 1) => {
      let src = new Uint8Array(data);
      let dst = new Uint8Array(src.length);
      const kernel = [1, 2, 1, 2, 4, 2, 1, 2, 1];
      for (let iter = 0; iter < iterations; iter += 1) {
        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            let sum = 0;
            let weight = 0;
            let k = 0;
            for (let ky = -1; ky <= 1; ky += 1) {
              const yy = Math.min(height - 1, Math.max(0, y + ky));
              for (let kx = -1; kx <= 1; kx += 1) {
                const xx = Math.min(width - 1, Math.max(0, x + kx));
                const w = kernel[k];
                sum += src[yy * width + xx] * w;
                weight += w;
                k += 1;
              }
            }
            dst[y * width + x] = sum / weight;
          }
        }
        const tmp = src;
        src = dst;
        dst = tmp;
      }
      return src;
    };

    const analyzeImage = async (file) => {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, MAX_GRID_RESOLUTION / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(2, Math.round(bitmap.width * scale));
      const height = Math.max(2, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(bitmap, 0, 0, width, height);
      const imageData = ctx.getImageData(0, 0, width, height);
      const src = imageData.data;
      const pixels = width * height;
      const heightMap = new Uint8Array(pixels);
      const colorMap = new Uint8Array(pixels * 3);
      for (let i = 0, p = 0; i < src.length; i += 4, p += 1) {
        const r = src[i];
        const g = src[i + 1];
        const b = src[i + 2];
        const a = src[i + 3] / 255;
        const luminance = Math.round((0.299 * r + 0.587 * g + 0.114 * b) * a + (1 - a) * 255);
        heightMap[p] = luminance;
        colorMap[p * 3] = r;
        colorMap[p * 3 + 1] = g;
        colorMap[p * 3 + 2] = b;
      }
      bitmap.close();
      const smoothed = smoothHeightMap(heightMap, width, height, 2);
      return {
        width,
        height,
        heightMap: uint8ToBase64(smoothed),
        colorMap: uint8ToBase64(colorMap),
        heightArray: smoothed,
        colorArray: colorMap,
      };
    };

    const render3DPreview = async (analysis) => {
      if (!viewer) return;
      const { heightMap, width, height, colorMap } = analysis;
      const heightArray = analysis.heightArray || base64ToUint8(heightMap);
      const colorArray =
        analysis.colorArray || (colorMap ? base64ToUint8(colorMap) : null);

      let modules;
      try {
        modules = await loadThree();
      } catch (err) {
        console.warn("[relief-customizer] three.js preview unavailable", err);
        disposeThree();
        if (viewer) viewer.hidden = true;
        return;
      }
      const { THREE, OrbitControls } = modules;

      const widthMm = WIDTH_MM;
      const heightMm = WIDTH_MM * (height / Math.max(width, 1));
      const depthMm = DEPTH_MM;
      const baseMm = BASE_MM;

      disposeThree();
      viewer.innerHTML = "";
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(viewer.clientWidth, Math.max(viewer.clientHeight, 1));
      viewer.appendChild(renderer.domElement);
      threeRenderer = renderer;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf3f4f6);
      const camera = new THREE.PerspectiveCamera(
        35,
        viewer.clientWidth / Math.max(viewer.clientHeight, 1),
        0.1,
        1000,
      );
      camera.position.set(widthMm * 0.7, depthMm * 4, heightMm * 1.4);
      camera.lookAt(new THREE.Vector3(widthMm / 2, baseMm + depthMm / 2, heightMm / 2));
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.target.set(widthMm / 2, baseMm, heightMm / 2);
      controls.update();

      const light1 = new THREE.DirectionalLight(0xffffff, 1.3);
      light1.position.set(1, 1.2, 0.8);
      scene.add(light1);
      const light2 = new THREE.DirectionalLight(0xffffff, 0.5);
      light2.position.set(-0.6, 1, -0.8);
      scene.add(light2);
      scene.add(new THREE.AmbientLight(0xffffff, 0.4));

      const geometry = new THREE.PlaneGeometry(widthMm, heightMm, width - 1, height - 1);
      const position = geometry.attributes.position;
      for (let i = 0; i < position.count; i += 1) {
        const h = Math.pow(heightArray[i] / 255, 1.25);
        position.setZ(i, baseMm + h * depthMm);
      }
      position.needsUpdate = true;
      geometry.rotateX(-Math.PI / 2);

      let material;
      if (colorArray) {
        const colors = [];
        for (let i = 0; i < position.count; i += 1) {
          const r = colorArray[i * 3] / 255;
          const g = colorArray[i * 3 + 1] / 255;
          const b = colorArray[i * 3 + 2] / 255;
          colors.push(r, g, b);
        }
        geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
        material = new THREE.MeshPhongMaterial({ vertexColors: true, shininess: 20 });
      } else {
        material = new THREE.MeshPhongMaterial({ color: 0xf2f2f2, shininess: 30 });
      }

      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      const animate = () => {
        if (!threeRenderer) return;
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();
      viewer.hidden = false;
      threeScene = scene;
      threeCamera = camera;
      viewerResizeHandler = () => {
        if (!threeRenderer || !threeCamera) return;
        const w = viewer.clientWidth;
        const h = Math.max(viewer.clientHeight, 1);
        threeRenderer.setSize(w, h);
        threeCamera.aspect = w / h;
        threeCamera.updateProjectionMatrix();
      };
      window.addEventListener("resize", viewerResizeHandler, { passive: true });
    };

    imageInput.addEventListener("change", async () => {
      if (!imageInput.files || imageInput.files.length === 0) return;
      originalFile = imageInput.files[0];
      preparedFile = originalFile;
      previewImg.hidden = true;
      if (thumbWrap) thumbWrap.hidden = true;
      if (viewer) {
        viewer.hidden = true;
        disposeThree();
      }
      await showCropper(originalFile);
    });

    applyCropBtn.addEventListener("click", async () => {
      if (!originalFile) return;
      try {
        preparedFile = await getCroppedFile();
        resetCropState();
        const previewUrl = URL.createObjectURL(preparedFile);
        previewImg.src = previewUrl;
        previewImg.hidden = false;
        setStatus("Crop applied. Submit to generate preview.");
      } catch (err) {
        console.error(err);
        setStatus("Crop failed. Please try again.");
      }
    });

    resetCropBtn.addEventListener("click", () => {
      if (!originalFile) return;
      resetCropState();
      previewImg.hidden = true;
      setStatus("Original image restored.");
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!preparedFile) {
        setStatus("Please choose an image first.");
        return;
      }
      try {
        setStatus("Uploading...");
        const formData = new FormData();
        formData.append("file", preparedFile);
        const uploadResp = await fetch(WORKER_UPLOAD_URL, {
          method: "POST",
          body: formData,
        });
        if (!uploadResp.ok) throw new Error(await uploadResp.text());
        const uploadJson = await uploadResp.json();
        const sourceKey = uploadJson.key;

        setStatus("Analyzing image...");
        latestAnalysis = await analyzeImage(preparedFile);
        await render3DPreview(latestAnalysis);

        setStatus("Generating relief...");
        const payload = {
          source_key: sourceKey,
          frame_style: frameSelect.value,
          width_px: latestAnalysis.width,
          height_px: latestAnalysis.height,
          height_map: latestAnalysis.heightMap,
          color_map: latestAnalysis.colorMap,
          width_mm: WIDTH_MM,
          depth_mm: DEPTH_MM,
          base_mm: BASE_MM,
        };
        const genResp = await fetch(WORKER_GENERATE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!genResp.ok) {
          setStatus("Generate failed.");
          console.error(await genResp.text());
          return;
        }
        const result = await genResp.json();
        console.debug("[relief-customizer] generate result", result);

        previewImg.hidden = false;
        if (thumbWrap && thumbImg && result.preview_key) {
          thumbImg.src = `${WORKER_BASE_URL}/r2/${result.preview_key}`; // 如后续需要，可提供仅供内部显示的缩略图链接
          thumbWrap.hidden = false;
        }

        addToCartBtn.dataset.reliefJobId = result.job_id;
        addToCartBtn.dataset.reliefFrame = frameSelect.value;
        addToCartBtn.dataset.reliefSource = sourceKey;
        addToCartBtn.dataset.reliefPreview = result.preview_key || "";
        addToCartBtn.dataset.reliefStl = result.stl_key || "";
        addToCartBtn.dataset.reliefPly = result.ply_key || "";

        addToCartBtn.hidden = false;
        setStatus("Preview ready.");
      } catch (err) {
        console.error(err);
        setStatus(`Something went wrong. ${err.message || ""}`);
      }
    });

    addToCartBtn.addEventListener("click", async () => {
      const jobId = addToCartBtn.dataset.reliefJobId;
      const frame = addToCartBtn.dataset.reliefFrame;
      const source = addToCartBtn.dataset.reliefSource;
      const previewKey = addToCartBtn.dataset.reliefPreview || "";
      const stlKey = addToCartBtn.dataset.reliefStl || "";
      const plyKey = addToCartBtn.dataset.reliefPly || "";
      if (!jobId) {
        setStatus("Generate a preview before adding to cart.");
        return;
      }
      if (!window.reliefProductVariantId || Number(window.reliefProductVariantId) <= 0) {
        setStatus("Product variant not configured. Please set window.reliefProductVariantId.");
        return;
      }

      setStatus("Adding to cart...");
      try {
        const payload = {
          items: [
            {
              id: Number(window.reliefProductVariantId),
              quantity: 1,
              properties: {
                "Relief Job": jobId,
                "Frame": frame,
                "Source Key": source,
                "Preview Key": previewKey,
                "STL Key": stlKey,
                "PLY Key": plyKey,
              },
            },
          ],
        };
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          let message = "add to cart failed";
          try {
            const data = await response.json();
            if (data?.description) message = data.description;
            else if (data?.message) message = data.message;
          } catch (err) {
            console.warn("[relief-customizer] cart add error (non-JSON)", err);
          }
          throw new Error(message);
        }
        setStatus("Added to cart.");
      } catch (err) {
        console.error(err);
        setStatus(`Add to cart failed. ${err.message || ""}`);
      }
    });

    initialized = true;
    console.info("[relief-customizer] initialized");
    return true;
  };

  if (!tryInit()) {
    document.addEventListener("DOMContentLoaded", tryInit, { once: true });
    document.addEventListener("shopify:section:load", tryInit);
    document.addEventListener("shopify:section:reorder", tryInit);
  }
})();
